import { mapIncentiveProgram } from "@features/dynamic-incentives/data/mappers";
import {
  DynamicIncentiveChallengeModel,
  HistoryDynamicIncentiveProgramModel,
  IncentiveProgramModel,
} from "@features/dynamic-incentives/data/models";
import {
  DynamicIncentiveChallengeEntity,
  DynamicIncentiveFeedback,
  DynamicIncentivesRepositoryContract,
  HistoryDynamicIncentiveProgramEntity,
  IncentiveProgramEntity,
} from "@features/dynamic-incentives/domain";
import { BaseRepository } from "@features/shared/data";
import { GenericObject } from "@taager/reinforcements";
import { DYNAMIC_INCENTIVES_API } from "../constants";
import {
  mapDynamicIncentiveChallenge,
  mapHistoryDynamicIncentiveProgram,
} from "../mappers/dynamic-incentive-challenge.mapper";

export class DynamicIncentivesRepository
  extends BaseRepository
  implements DynamicIncentivesRepositoryContract
{
  /**
   * Get challenges list
   */
  public async listChallenges(): Promise<DynamicIncentiveChallengeEntity[]> {
    return this.runtimeCacheable(`dynamic-incentives.challenges`, async () => {
      const response = await this.http.get<DynamicIncentiveChallengeModel[]>(
        DYNAMIC_INCENTIVES_API.CHALLENGES,
      );

      return response.data.map(mapDynamicIncentiveChallenge);
    });
  }

  /**
   * Get challenges history
   */
  public async challengesHistory(): Promise<
    HistoryDynamicIncentiveProgramEntity[]
  > {
    const response = await this.http.get<HistoryDynamicIncentiveProgramModel[]>(
      DYNAMIC_INCENTIVES_API.CHALLENGES_HISTORY,
    );

    return response.data.map(mapHistoryDynamicIncentiveProgram);
  }

  /**
   * Accept a challenge
   */
  public async acceptChallenge(
    challengeId: string,
  ): Promise<DynamicIncentiveChallengeEntity> {
    const response = await this.http.post<
      GenericObject,
      DynamicIncentiveChallengeModel
    >(DYNAMIC_INCENTIVES_API.ACCEPT_DYNAMIC_INCENTIVE_PROGRAM(challengeId), {});

    return mapDynamicIncentiveChallenge(response.data);
  }

  /**
   * Decline a challenge
   */
  public async declineChallenge(challengeId: string): Promise<void> {
    await this.http.post<GenericObject, void>(
      DYNAMIC_INCENTIVES_API.DECLINE_DYNAMIC_INCENTIVE_PROGRAM(challengeId),
      {},
    );
  }

  /**
   * Submit feedback for a challenge
   */
  public async submitChallengeFeedback(
    data: DynamicIncentiveFeedback,
  ): Promise<void> {
    await this.http.post<DynamicIncentiveFeedback, void>(
      DYNAMIC_INCENTIVES_API.FEEDBACK_DYNAMIC_INCENTIVE_PROGRAM(data.id),
      data,
    );
  }

  /**
   * Get incentive program
   */
  public async getIncentiveProgram(): Promise<IncentiveProgramEntity> {
    const response = await this.http.get<IncentiveProgramModel>(
      DYNAMIC_INCENTIVES_API.INCENTIVE_PROGRAM,
    );

    return mapIncentiveProgram(response.data);
  }
}
